import { Input, Row, Typography, Divider, Col, Space, Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { userService } from "../../services";
import "./index.less";

interface UserIdentificationPublicationModalComponentProps {
  isOpen: boolean;
  onSave: (text: string) => void;
}

const UserIdentificationPublicationModalComponent = ({ isOpen, onSave }: UserIdentificationPublicationModalComponentProps) => {
  const { t } = useTranslation();
  const [editText, setEditText] = useState<string>("");
  const [isIdentifier, setIsIdentifier] = useState<boolean>(false);
  const [identifiers, setIdentifiers] = useState<any>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [validMentions, setValidMentions] = useState<Set<string>>(new Set());
  const textRef = useRef<any>(null);

  useEffect(() => {
    const textareaElement = textRef.current?.resizableTextArea?.textArea;
    const handleTextAreaTouchMove = (e: TouchEvent) => {
      e.preventDefault();
    };

    if (isOpen) {
      if (textareaElement) {
        textareaElement.addEventListener("touchmove", handleTextAreaTouchMove, { passive: false });
      }
    } else {
      if (textareaElement) {
        textareaElement.removeEventListener("touchmove", handleTextAreaTouchMove);
      }
    }

    return () => {
      if (textareaElement) {
        textareaElement.removeEventListener("touchmove", handleTextAreaTouchMove);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && textRef.current) {
      const textareaElement = textRef.current.resizableTextArea.textArea;
      textareaElement.focus();

      const textLength = textareaElement.value.length;
      textareaElement.setSelectionRange(textLength, textLength);
    }
  }, [isOpen]);

  useEffect(() => {
    const words = editText?.match(/(\S+|\s+)/g) || [];
    const lastWord = words[words.length - 1];

    if (lastWord) {
      if (lastWord === "@") {
        setIsIdentifier(true);
      } else if (lastWord.startsWith("@") && lastWord.length > 1) {
        setIsIdentifier(true);
        const wordWithoutAt = lastWord.slice(1);
        if (wordWithoutAt) {
          searchUser(wordWithoutAt);
        } else {
          setIdentifiers([]);
        }
      } else {
        setIdentifiers([]);
        setIsIdentifier(false);
      }
    } else {
      setIdentifiers([]);
      setIsIdentifier(false);
    }
  }, [editText]);

  const handleIdentifierClick = () => {
    const trimmedText = editText.trimEnd();
    const words = trimmedText.split(/\s+/);
    const lastWord = words[words.length - 1];
    const secondLastWord = words[words.length - 2] || "";

    if (lastWord === "" && secondLastWord === "@") {
      setEditText(`${trimmedText.slice(0, -1)} @`);
    } else if (lastWord !== "") {
      setEditText(`${editText} @`);
    } else if (editText === "") {
      setEditText("@");
    }

    textRef.current.focus();
  };

  const searchUser = async (value: string) => {
    setIsSearching(true);
    const payload = {
      q: value,
      limit: 10,
    };
    await userService
      .search(payload)
      .then((res) => {
        setIdentifiers(res.data);
        setIsSearching(false);
      })
      .catch((err: any) => {
        console.log(err?.message);
        setIsSearching(false);
      });
  };

  const handleProfileClick = (username: string) => {
    setValidMentions((prev) => new Set(prev).add(username));

    const words = editText?.match(/(\S+|\s+)/g) || [];
    if (words.length > 0) {
      const lastWord = words[words.length - 1];
      const updatedText = editText.replace(new RegExp(`${lastWord}$`), `@${username} `);
      setEditText(updatedText);
      textRef.current.focus();
    }
  };

  const cleanInvalidMentions = (text: string, validUsernames: string[]) => {
    return text
      .split(/(\s+)/)
      .map((word) => {
        if (word.startsWith("@")) {
          const username = word.slice(1).trim();
          if (!username) return "";
          if (!validUsernames.includes(username)) return "";
          return word;
        }
        return word;
      })
      .join("")
      .replace(/\s+/g, " ")
      .trim();
  };

  const handleOverlayClick = () => {
    const cleanedText = cleanInvalidMentions(editText, Array.from(validMentions));
    onSave(cleanedText);
  };

  const handleModalClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div className="post-identification-modal-overlay no-scroll" onClick={handleOverlayClick}>
      <div className="post-identification-modal-content" onClick={handleModalClick}>
        <div className="post-identification-modal-header">
          <Typography.Text className="header-title-black-20">{t("caption")}</Typography.Text>
          <Typography.Text className="ok-text" onClick={handleOverlayClick}>
            {t("OK")}
          </Typography.Text>
        </div>
        <Divider className="publication-divider" />
        <Row className="caption-input-container">
          <Input.TextArea
            className="captionInput"
            placeholder={`${t("write-caption-tag-creators-text")}`}
            autoSize={{ minRows: 3, maxRows: 6 }}
            bordered={false}
            onChange={(e) => setEditText(e.target.value)}
            ref={textRef}
            value={editText}
            inputMode="search"
          />
          {!isIdentifier && (
            <Typography.Text className="identify-btn" onClick={handleIdentifierClick}>
              @ {t("identify")}
            </Typography.Text>
          )}
        </Row>

        {isIdentifier && (
          <Row className="identifiers-list-container">
            {isSearching ? (
              <Col span={24}>
                {[...Array(3)].map((_, index) => (
                  <Row key={index} className="justify-between items-center mt-10">
                    <Col span={24}>
                      <Row className="items-center">
                        <Skeleton.Avatar active size={40} shape="circle" style={{ background: "#333" }} />
                        <Space direction="vertical" className="gap-5" style={{ marginLeft: 12 }}>
                          <Skeleton.Input active size="small" style={{ width: 100, background: "#333" }} />
                          <Skeleton.Input active size="small" style={{ width: 150, background: "#333" }} />
                        </Space>
                      </Row>
                    </Col>
                  </Row>
                ))}
              </Col>
            ) : identifiers?.length !== 0 ? (
              <>
                {identifiers.map((creator: any) => (
                  <Col span={24} key={creator._id} className="creator-item">
                    <Row onClick={() => handleProfileClick(creator.username!)} className="creator-row">
                      <img className="avatar" src={creator.avatar || ""} alt={creator.avatar || "Creator Avatar"} />
                      <div className="creator-info">
                        <Typography.Text className="username">{t(creator.username || "")}</Typography.Text>
                        <Typography.Text className="name">{creator.name}</Typography.Text>
                      </div>
                    </Row>
                  </Col>
                ))}
              </>
            ) : (
              <Typography.Text className="no-creator-found">{t("no-creator-found")}</Typography.Text>
            )}
          </Row>
        )}
      </div>
    </div>
  );
};

export default UserIdentificationPublicationModalComponent;
